import { flow } from 'fp-ts/function';
import { __, filter, get, has, head, map, nth } from 'lodash/fp';
import { createSelector } from 'reselect';
import { Route, RouteConfig } from '@mews-distributor/common';
import { RouteConfiguration } from '@mews-distributor/common/src/enums/Route';
import { getCurrentRoute } from '../../../modules/app/selectors';
import { isMultiHotel } from '../../../modules/configuration/selectors';
import { hasAvailabilityBlocksFailed } from '../../../modules/entities/availabilityBlocks/selectors';
import { hasExceedingResourceOrders, hasReservation } from '../../../modules/entities/reservations/selectors';
import { isSelectedHotelCitySelected } from '../../../modules/hotels/selectors';
import {
    hasSelectedEnterprisePaymentConfigurationFailed,
    isSelectedEnterprisePaymentConfigurationFetching,
} from '../../../modules/paymentConfigurations/selectors';
import { areDatesSelected, getResourceOrder, isHotelSelected } from '../../../modules/resourceOrder/selectors';
import { ApplicationState } from '../../../types';
import { isCheckoutFetching, isCheckoutPreparing } from '../../../views/checkout/data/selectors';

export const isMenuEnabled = createSelector(
    isCheckoutFetching,
    isCheckoutPreparing,
    getCurrentRoute,
    (checkoutFetching, checkoutPreparing, route) =>
        !checkoutFetching && !checkoutPreparing && route !== Route.Confirmation
);

export const getTabs = createSelector(isMultiHotel, (multiHotel) =>
    multiHotel
        ? [Route.Dates, Route.Properties, Route.SpaceCategories, Route.Rates, Route.Summary, Route.Checkout]
        : [Route.Dates, Route.SpaceCategories, Route.Rates, Route.Summary, Route.Checkout]
);

const isRoomSelected = createSelector(getResourceOrder, has('roomId'));

const isHotelTabEnabled = areDatesSelected;

const isRoomsTabEnabled = createSelector(
    areDatesSelected,
    isMultiHotel,
    isHotelSelected,
    isSelectedHotelCitySelected,
    (datesSelected, multiHotel, hotelSelected, hotelsCitySelected) =>
        datesSelected && (!multiHotel || (hotelSelected && hotelsCitySelected))
);

const isRatesTabEnabled = createSelector(
    areDatesSelected,
    isRoomSelected,
    isSelectedHotelCitySelected,
    (datesSelected, roomSelected, hotelsCitySelected) => datesSelected && roomSelected && hotelsCitySelected
);

const isCheckoutTabEnabled = createSelector(
    hasSelectedEnterprisePaymentConfigurationFailed,
    isSelectedEnterprisePaymentConfigurationFetching,
    (hasPaymentConfigurationFailed, isPaymentConfigurationFetching) =>
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        !hasPaymentConfigurationFailed && !isPaymentConfigurationFetching
);

const isCheckoutEnabled = createSelector(
    isCheckoutTabEnabled,
    hasReservation,
    hasExceedingResourceOrders,
    (checkoutTabEnabled, anyReservation, hasExceedingOrders) =>
        checkoutTabEnabled && anyReservation && !hasExceedingOrders
);

export const getEnabledTabs = createSelector(
    isHotelTabEnabled,
    isRoomsTabEnabled,
    isRatesTabEnabled,
    hasReservation,
    isCheckoutEnabled,
    (hotelTabEnabled, roomsTabEnabled, ratesTabEnabled, anyReservation, checkoutEnabled) => [
        [Route.Dates, true],
        [Route.Properties, hotelTabEnabled],
        [Route.SpaceCategories, roomsTabEnabled],
        [Route.Rates, ratesTabEnabled],
        [Route.Summary, anyReservation],
        [Route.Checkout, checkoutEnabled],
    ]
);

const getCompletedTabs = createSelector(
    areDatesSelected,
    isRoomsTabEnabled,
    isRatesTabEnabled,
    hasReservation,
    isCheckoutEnabled,
    (datesSelected, roomsTabEnabled, ratesTabEnabled, anyReservation, checkoutEnabled) => [
        [Route.Dates, datesSelected],
        [Route.Properties, roomsTabEnabled],
        [Route.SpaceCategories, ratesTabEnabled],
        [Route.Rates, anyReservation],
        [Route.Summary, checkoutEnabled],
        [Route.Checkout, false],
    ]
);

const getTabsList = (tabs: $TSFixMe): ((state: ApplicationState) => RouteConfiguration[]) =>
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    createSelector(tabs, flow(filter(nth(1)), map(flow(head, get(__, RouteConfig)))));

export const getEnabledTabsList = getTabsList(getEnabledTabs);
export const getCompletedTabsList = getTabsList(getCompletedTabs);

export const isStepbarVisible = createSelector(
    getCurrentRoute,
    hasAvailabilityBlocksFailed,
    (route, availabilityBlockFailed) => route !== Route.Confirmation && !availabilityBlockFailed
);

// @ts-expect-error $TSFixMeError: Type 'undefined' cannot be used as an index type.
export const getActiveTab = createSelector(getCurrentRoute, (route) => RouteConfig[route]);

export const showPropertyInfo = createSelector(
    isMultiHotel,
    getCurrentRoute,
    (multiHotel, currentRoute) => !(multiHotel && (currentRoute === Route.Dates || currentRoute === Route.Properties))
);

export const showCurrencySelect = createSelector(getCurrentRoute, (route) => route !== Route.Confirmation);
